import React, {
  useState,
  useEffect,
  useContext,
  useCallback,
  useMemo,
} from "react";
import {
  AppBar,
  Box,
  Button,
  Card,
  Container,
  Divider,
  Fade,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Stack,
  TextField,
  Toolbar,
  Typography,
  useTheme,
  alpha,
} from "@mui/material";
import {
  Add,
  Delete,
  Edit,
  KeyboardArrowDown,
  KeyboardArrowUp,
  Language,
  Save,
  Search,
} from "@mui/icons-material";
import { GlobalContext } from "../../context/GlobalContext";
import PageHeader from "../../common/PageHeader";
import CommonDialog from "../../common/CommonDialog";
import debounce from "lodash/debounce";

const Translation = ({ lang }) => {
  const { hitAxios } = useContext(GlobalContext);
  const theme = useTheme();

  const [langs, setLangs] = useState([]);
  const [newCode, setNewCode] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [defaultLang, setDefaultLang] = useState(null);

  // ── Edit dialog state ──────────────────────────────────────────
  const [editOpen, setEditOpen] = useState(false);
  const [langCode, setLangCode] = useState("");
  const [editLang, setEditLang] = useState([]);
  const [filteredLang, setFilteredLang] = useState([]);
  const [unsaved, setUnsaved] = useState(false);
  const [saving, setSaving] = useState(false);

  // ── Search / sort / pagination ─────────────────────────────────
  const [search, setSearch] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);

  async function getDefaultLang() {
    const res = await hitAxios({
      path: "/api/web/get-default-language",
      admin: false,
      post: false,
    });
    if (res.data.success) setDefaultLang(res.data.default);
  }

  async function setAsDefault(code) {
    const res = await hitAxios({
      path: "/api/web/set-default-language",
      admin: true,
      post: true,
      obj: { code },
    });
    if (res.data.success) setDefaultLang(code);
  }

  // ─── Fetch list ────────────────────────────────────────────────
  async function getLangs() {
    const res = await hitAxios({
      path: "/api/web/get-all-translation-name",
      admin: false,
      post: false,
    });
    if (res.data.success) setLangs(res.data.data);
  }

  // ─── Delete ────────────────────────────────────────────────────
  async function delOne(code) {
    if (!window.confirm(lang?.AUS || "Are you sure?")) return;
    const res = await hitAxios({
      path: "/api/web/del-one-translation",
      admin: true,
      post: true,
      obj: { code },
    });
    if (res.data.success) getLangs();
  }

  // ─── Add new ───────────────────────────────────────────────────
  async function addNewLang() {
    if (!newCode.trim()) return;
    const res = await hitAxios({
      path: "/api/web/add-new-translation",
      post: true,
      admin: true,
      obj: { newcode: newCode },
    });
    if (res.data.success) {
      setAddOpen(false);
      setNewCode("");
      getLangs();
    }
  }

  // ─── Open edit ─────────────────────────────────────────────────
  async function openEdit(code) {
    const res = await hitAxios({
      path: `/api/web/get-one-translation?code=${code}`,
      admin: false,
      post: false,
    });
    if (res.data.success) {
      const parsed = Object.entries(res.data.data).map(([k, v]) => ({
        var: k,
        name: v,
      }));
      setEditLang(parsed);
      setFilteredLang(parsed);
      setLangCode(code);
      setPage(1);
      setSearch("");
      setUnsaved(false);
      setEditOpen(true);
    }
  }

  // ─── Save translation ──────────────────────────────────────────
  async function updateLang() {
    setSaving(true);
    const obj = {};
    editLang.forEach((item) => (obj[item.var] = item.name));
    const res = await hitAxios({
      path: "/api/web/update-one-translation",
      post: true,
      admin: true,
      obj: { code: langCode, updatedjson: obj },
    });
    if (res.data.success) setUnsaved(false);
    setSaving(false);
  }

  // ─── Close edit (guard unsaved) ────────────────────────────────
  function handleEditClose() {
    if (
      unsaved &&
      !window.confirm(
        lang?.unsavedChangesWarning ||
          "You have unsaved changes. Are you sure you want to close?",
      )
    )
      return;
    setEditOpen(false);
    setUnsaved(false);
  }

  // ─── Search (debounced) ────────────────────────────────────────
  const applySearch = useCallback(
    debounce((val) => {
      const f = editLang.filter(
        (item) =>
          item.var.toLowerCase().includes(val.toLowerCase()) ||
          String(item.name).toLowerCase().includes(val.toLowerCase()),
      );
      setFilteredLang(f);
      setPage(1);
    }, 300),
    [editLang],
  );

  function onSearchChange(e) {
    setSearch(e.target.value);
    applySearch(e.target.value);
  }

  // ─── Sort ──────────────────────────────────────────────────────
  function toggleSort() {
    const next = sortOrder === "asc" ? "desc" : "asc";
    setSortOrder(next);
    setFilteredLang((prev) =>
      [...prev].sort((a, b) =>
        next === "asc"
          ? a.var.localeCompare(b.var)
          : b.var.localeCompare(a.var),
      ),
    );
  }

  // ─── Field change ──────────────────────────────────────────────
  function handleFieldChange(varKey, value) {
    setEditLang((prev) =>
      prev.map((item) =>
        item.var === varKey ? { ...item, name: value } : item,
      ),
    );
    setFilteredLang((prev) =>
      prev.map((item) =>
        item.var === varKey ? { ...item, name: value } : item,
      ),
    );
    setUnsaved(true);
  }

  // ─── Pagination slice ──────────────────────────────────────────
  const pageItems = useMemo(() => {
    const start = (page - 1) * perPage;
    return filteredLang.slice(start, start + perPage);
  }, [filteredLang, page, perPage]);

  const totalPages = Math.ceil(filteredLang.length / perPage);

  useEffect(() => {
    getLangs();
    getDefaultLang();
  }, []);

  return (
    <Box>
      <PageHeader
        icon={Language}
        title={lang?.webTranslation || "Translation Management"}
        subtitle={
          lang?.webTransDes || "Manage your application's language translations"
        }
        primaryAction={
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setAddOpen(true)}
            size="large"
          >
            {lang?.addNewLanguage || "Add New Language"}
          </Button>
        }
      />

      {/* ── Language list ── */}
      <Stack spacing={2}>
        {langs.length === 0 ? (
          <Paper
            elevation={0}
            sx={{
              p: 4,
              borderRadius: 3,
              border: `1px solid ${theme.palette.divider}`,
              textAlign: "center",
            }}
          >
            <Typography color="text.secondary" mb={2}>
              {lang?.noLanguagesAvailable || "No languages available"}
            </Typography>
            <Button
              variant="outlined"
              startIcon={<Add />}
              onClick={() => setAddOpen(true)}
              sx={{ borderRadius: 2 }}
            >
              {lang?.addNewLanguage || "Add New Language"}
            </Button>
          </Paper>
        ) : (
          langs.map((file) => {
            const code = file.replace(".json", "");
            const isDefault = defaultLang === code;

            return (
              <Card
                key={code}
                elevation={0}
                sx={{
                  border: `1px solid ${
                    isDefault
                      ? theme.palette.primary.main
                      : theme.palette.divider
                  }`,
                  borderRadius: 3,
                  px: 2.5,
                  py: 1.75,
                  transition: "box-shadow 0.2s",
                  "&:hover": { boxShadow: 2 },
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  {/* Left side — icon + code + default badge */}
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Box
                      sx={{
                        width: 38,
                        height: 38,
                        borderRadius: 1.5,
                        bgcolor: alpha(
                          isDefault
                            ? theme.palette.primary.main
                            : theme.palette.primary.main,
                          isDefault ? 0.2 : 0.1,
                        ),
                        color: theme.palette.primary.main,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Language fontSize="small" />
                    </Box>
                    <Box>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography fontWeight={700}>{code}</Typography>
                        {isDefault && (
                          <Box
                            sx={{
                              px: 1,
                              py: 0.2,
                              borderRadius: 1,
                              bgcolor: alpha(theme.palette.primary.main, 0.12),
                              color: theme.palette.primary.main,
                              fontSize: 10,
                              fontWeight: 700,
                              letterSpacing: 0.5,
                              lineHeight: 1.8,
                            }}
                          >
                            {lang?.default || "DEFAULT"}
                          </Box>
                        )}
                      </Stack>
                      <Typography variant="caption" color="text.secondary">
                        {lang?.languageCode || "Language Code"}
                      </Typography>
                    </Box>
                  </Stack>

                  {/* Right side — action buttons */}
                  <Stack direction="row" spacing={1.5}>
                    {/* Set Default button — only show if not already default */}
                    {!isDefault && (
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => setAsDefault(code)}
                        sx={{ borderRadius: 2 }}
                      >
                        {lang?.setDefault || "Set Default"}
                      </Button>
                    )}
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<Edit />}
                      onClick={() => openEdit(code)}
                      sx={{ borderRadius: 2 }}
                    >
                      {lang?.edit || "Edit"}
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      color="error"
                      startIcon={<Delete />}
                      onClick={() => delOne(code)}
                      sx={{ borderRadius: 2 }}
                    >
                      {lang?.delete || "Delete"}
                    </Button>
                  </Stack>
                </Stack>
              </Card>
            );
          })
        )}
      </Stack>

      {/* ── Add new language dialog ── */}
      <CommonDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        title={lang?.addNewLanguage || "Add New Language"}
        icon={Language}
        maxWidth="sm"
        fullWidth
      >
        <Stack spacing={2.5} p={2.5}>
          <TextField
            fullWidth
            size="small"
            label={lang?.enterNewLangugename || "Language Code"}
            helperText={
              lang?.addLangFiled || "Enter language code (e.g. en, fr, es)"
            }
            value={newCode}
            onChange={(e) => setNewCode(e.target.value.replace(/\s/g, ""))}
            InputProps={{
              startAdornment: (
                <Language
                  sx={{ fontSize: 18, color: "text.disabled", mr: 1 }}
                />
              ),
            }}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
          />
          <Divider />
          <Box display="flex" justifyContent="flex-end" gap={1.5}>
            <Button
              variant="outlined"
              color="inherit"
              onClick={() => setAddOpen(false)}
              sx={{ borderRadius: 2 }}
            >
              {lang?.close || "Cancel"}
            </Button>
            <Button
              variant="contained"
              startIcon={<Add />}
              disabled={!newCode.trim()}
              onClick={addNewLang}
              sx={{ borderRadius: 2 }}
            >
              {lang?.submit || "Add Language"}
            </Button>
          </Box>
        </Stack>
      </CommonDialog>

      {/* ── Full-screen edit dialog ── */}
      <CommonDialog
        open={editOpen}
        onClose={handleEditClose}
        fullScreen
        title={`${lang?.edit || "Edit"} — ${langCode}`}
        icon={Language}
        // Override AppBar actions via children — we render our own AppBar
      >
        {/* Custom AppBar inside fullScreen dialog */}
        <AppBar
          position="sticky"
          color="default"
          elevation={1}
          sx={{ bgcolor: theme.palette.background.paper, top: 0 }}
        >
          <Toolbar>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              width="100%"
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                <IconButton
                  size="small"
                  onClick={handleEditClose}
                  sx={{
                    bgcolor: alpha(theme.palette.text.secondary, 0.08),
                    "&:hover": {
                      bgcolor: alpha(theme.palette.text.secondary, 0.15),
                    },
                  }}
                >
                  {/* Close icon rendered by CommonDialog header — we just need the save button here */}
                </IconButton>
                <Box>
                  <Typography variant="subtitle1" fontWeight={700}>
                    {lang?.edit || "Edit"} — {langCode}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {filteredLang.length} {lang?.entries || "entries"}
                  </Typography>
                </Box>
              </Stack>

              <Button
                variant="contained"
                size="small"
                startIcon={<Save />}
                onClick={updateLang}
                disabled={saving || !unsaved}
                sx={{ borderRadius: 2, px: 2.5 }}
              >
                {saving
                  ? lang?.saving || "Saving..."
                  : unsaved
                    ? lang?.save || "Save Changes"
                    : lang?.saved || "Saved"}
              </Button>
            </Stack>
          </Toolbar>
        </AppBar>

        <Container maxWidth="lg" sx={{ py: 3 }}>
          <Stack spacing={3}>
            {/* Search + controls */}
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 3,
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                alignItems={{ sm: "center" }}
                justifyContent="space-between"
              >
                <TextField
                  fullWidth
                  size="small"
                  placeholder={lang?.search || "Search keys or values..."}
                  value={search}
                  onChange={onSearchChange}
                  InputProps={{
                    startAdornment: (
                      <Search
                        sx={{ fontSize: 18, color: "text.disabled", mr: 1 }}
                      />
                    ),
                  }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />
                <Stack direction="row" spacing={1.5} sx={{ flexShrink: 0 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={toggleSort}
                    startIcon={
                      sortOrder === "asc" ? (
                        <KeyboardArrowUp />
                      ) : (
                        <KeyboardArrowDown />
                      )
                    }
                    sx={{ borderRadius: 2 }}
                  >
                    {sortOrder === "asc"
                      ? lang?.sortAsc || "A–Z"
                      : lang?.sortDesc || "Z–A"}
                  </Button>
                  <FormControl size="small" sx={{ minWidth: 130 }}>
                    <InputLabel>{lang?.itemsPerPage || "Per page"}</InputLabel>
                    <Select
                      value={perPage}
                      label={lang?.itemsPerPage || "Per page"}
                      onChange={(e) => {
                        setPerPage(Number(e.target.value));
                        setPage(1);
                      }}
                      sx={{ borderRadius: 2 }}
                    >
                      {[10, 20, 50, 100].map((n) => (
                        <MenuItem key={n} value={n}>
                          {n}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>
              </Stack>
            </Paper>

            {/* Stats */}
            <Typography variant="caption" color="text.secondary">
              {lang?.showing || "Showing"}{" "}
              {Math.min((page - 1) * perPage + 1, filteredLang.length)}–
              {Math.min(page * perPage, filteredLang.length)} {lang?.of || "of"}{" "}
              {filteredLang.length} {lang?.items || "items"}
              {search &&
                ` (${lang?.filtered || "filtered"} ${lang?.from || "from"} ${editLang.length})`}
            </Typography>

            {/* Entries */}
            {filteredLang.length === 0 ? (
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  borderRadius: 3,
                  border: `1px solid ${theme.palette.divider}`,
                  textAlign: "center",
                }}
              >
                <Typography color="text.secondary">
                  {search
                    ? lang?.noSearchResults ||
                      "No matching translation keys found"
                    : lang?.noTranslations || "No translation keys available"}
                </Typography>
              </Paper>
            ) : (
              <Stack spacing={1.5}>
                {pageItems.map((item) => {
                  const isColor =
                    item.var === "color_theme" || item.var === "color_front";
                  return (
                    <Paper
                      key={item.var}
                      elevation={0}
                      sx={{
                        p: 2,
                        borderRadius: 2.5,
                        border: `1px solid ${theme.palette.divider}`,
                        transition: "box-shadow 0.15s",
                        "&:hover": { boxShadow: 1 },
                      }}
                    >
                      <Stack spacing={0.75}>
                        <Typography
                          variant="caption"
                          fontWeight={600}
                          color="text.disabled"
                          sx={{ ml: 0.5 }}
                        >
                          {item.var}
                        </Typography>
                        <TextField
                          fullWidth
                          size="small"
                          value={item.name}
                          multiline={String(item.name).length > 50}
                          rows={String(item.name).length > 100 ? 3 : 1}
                          onChange={(e) =>
                            handleFieldChange(item.var, e.target.value)
                          }
                          helperText={isColor ? lang?.addHexOnly : undefined}
                          InputProps={{
                            ...(isColor && {
                              endAdornment: (
                                <InputAdornment position="end">
                                  <Box
                                    sx={{
                                      width: 22,
                                      height: 22,
                                      borderRadius: 1,
                                      bgcolor: item.name,
                                      border: "1px solid",
                                      borderColor: "divider",
                                    }}
                                  />
                                </InputAdornment>
                              ),
                            }),
                          }}
                          sx={{
                            "& .MuiOutlinedInput-root": { borderRadius: 2 },
                          }}
                        />
                      </Stack>
                    </Paper>
                  );
                })}
              </Stack>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <Box
                display="flex"
                justifyContent="center"
                sx={{ position: "sticky", bottom: 16, zIndex: 5 }}
              >
                <Paper elevation={3} sx={{ borderRadius: 5, px: 2, py: 0.75 }}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(_, v) => setPage(v)}
                    color="primary"
                    showFirstButton
                    showLastButton
                    size="large"
                  />
                </Paper>
              </Box>
            )}

            <Box pb={8} />
          </Stack>
        </Container>

        {/* Floating save */}
        <Fade in={unsaved}>
          <Box
            sx={{
              position: "fixed",
              bottom: 24,
              right: 24,
              zIndex: theme.zIndex.drawer + 2,
            }}
          >
            <Button
              variant="contained"
              startIcon={<Save />}
              onClick={updateLang}
              disabled={saving}
              sx={{ borderRadius: 8, px: 3, py: 1.5, boxShadow: 4 }}
            >
              {saving
                ? lang?.saving || "Saving..."
                : lang?.save || "Save Changes"}
            </Button>
          </Box>
        </Fade>
      </CommonDialog>
    </Box>
  );
};

export default Translation;
